import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { artifactKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  listArtifactsContract,
  showArtifactContract,
  type Artifact,
  type ListArtifactsResponse,
  type ShowArtifactResponse
} from '@bliks-io/contracts';

/**
 * Fetch artifacts list
 *
 * @example
 * ```tsx
 * function ArtifactsList() {
 *   const { data: artifacts, isLoading} = useArtifacts();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!artifacts) return null;
 *
 *   return <List items={artifacts} />;
 * }
 * ```
 */
export function useArtifacts() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: artifactKeys.list(),
    queryFn: async ({ signal }): Promise<Artifact[]> => {
      const { data } = await apiClient.get<ListArtifactsResponse>(listArtifactsContract.path, {
        signal
      });
      return data.data;
    },
    enabled: !!token
  });
}

export const artifactQueryOptions = (id: string) =>
  queryOptions({
    queryKey: artifactKeys.detail(id),
    queryFn: async ({ signal }): Promise<Artifact> => {
      // Detail pages must load soft-deleted artifacts too (direct URL,
      // links from old recordings); deleted ones carry deletedAt.
      const { data } = await apiClient.get<ShowArtifactResponse>(
        `${showArtifactContract.path.replace(':id', id)}?includeDeleted=true`,
        { signal }
      );
      return data.data;
    }
  });

/**
 * Fetch a single artifact by ID
 *
 * @example
 * ```tsx
 * function ArtifactDetail({ id }: { id: string }) {
 *   const { data: artifact, isLoading } = useArtifact(id);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!artifact) return <NotFound />;
 *
 *   return <DetailView artifact={artifact} />;
 * }
 * ```
 */
export function useArtifact(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...artifactQueryOptions(id),
    enabled: !!token && !!id
  });
}
